// Cancellation Report — every cancelled + no-show appointment in the
// window with reason, note, and who initiated. Left side shows a
// reason-frequency summary so managers can spot patterns fast.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const STATUS_TONE = {
  cancelled: "bg-rose-100 text-rose-900 border-rose-200",
  no_show: "bg-amber-100 text-amber-900 border-amber-200",
};

export default function CancellationReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_month");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/cancellations`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const exportSpec = useMemo(() => {
    if (!data) return null;
    const rows = (data.rows || []).map((r) => ({
      cancelled_at: fmtTime(r.cancelled_at),
      status: r.status,
      client: r.customer_name,
      service: r.service_name,
      practitioner: r.staff_name,
      reason: r.reason,
      note: r.note,
      by: r.cancelled_by,
      source: r.source_label,
    }));
    return {
      filename: "cancellations",
      range: { date_from: data.date_from, date_to: data.date_to },
      subtitle: `${data.count} cancellation${data.count === 1 ? "" : "s"}`,
      columns: [
        { header: "Cancelled", key: "cancelled_at" },
        { header: "Status", key: "status" },
        { header: "Client", key: "client" },
        { header: "Service", key: "service" },
        { header: "Practitioner", key: "practitioner" },
        { header: "Reason", key: "reason" },
        { header: "Note", key: "note" },
        { header: "By", key: "by" },
        { header: "Source", key: "source" },
      ],
      rows,
    };
  }, [data]);

  return (
    <ReportShell title="Cancellation Report" subtitle="Every cancelled + no-show visit with reason, note and who initiated." branchName={branch?.name} tab="customers" exportSpec={exportSpec}>
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="rounded-lg overflow-hidden lg:col-span-1" data-testid="cancellations-summary">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border flex items-baseline justify-between">
                <h2 className="text-sm font-semibold">By Reason</h2>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">total</div>
                  <div className="text-lg font-semibold">{data?.count ?? 0}</div>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="uppercase text-[10px] tracking-wider">Reason</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!data || data.reasons.length === 0) ? (
                    <TableRow><TableCell colSpan={2} className="text-center text-sm text-muted-foreground py-6">
                      {loading ? "Loading…" : "None"}
                    </TableCell></TableRow>
                  ) : data.reasons.map((r) => (
                    <TableRow key={r.reason} data-testid={`cancel-reason-${r.reason}`}>
                      <TableCell className="text-sm truncate max-w-xs" title={r.reason}>{r.reason}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{r.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="rounded-lg overflow-hidden lg:col-span-2" data-testid="cancellations-rows-card">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold">All Cancellations</h2>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="uppercase text-[10px] tracking-wider">Cancelled</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Status</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Client</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Service</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Practitioner</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Reason</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Source</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!data || data.count === 0) ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                      {loading ? "Loading…" : "No cancellations in this range."}
                    </TableCell></TableRow>
                  ) : data.rows.map((r) => (
                    <TableRow key={r.id} data-testid={`cancel-row-${r.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtTime(r.cancelled_at)}</TableCell>
                      <TableCell><Badge className={`rounded-full text-[10px] ${STATUS_TONE[r.status] || ""}`}>{r.status}</Badge></TableCell>
                      <TableCell className="text-sm">
                        {r.customer_id ? (
                          <Link to={`/staff/brand/customers/${r.customer_id}`} className="text-primary hover:underline">{r.customer_name}</Link>
                        ) : r.customer_name}
                      </TableCell>
                      <TableCell className="text-sm max-w-xs truncate" title={r.service_name}>{r.service_name}</TableCell>
                      <TableCell className="text-sm">{r.staff_name}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate" title={r.reason}>
                        {r.reason}
                        {r.note ? <span className="block text-[11px] text-muted-foreground truncate" title={r.note}>{r.note}</span> : null}
                      </TableCell>
                      <TableCell className="text-xs" data-testid={`cancel-source-${r.id}`}>
                        <span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-900" title={r.source_label}>
                          {r.source_label}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs" data-testid={`cancel-by-${r.id}`}>
                        <span className="block">{r.cancelled_by_name || <span className="text-muted-foreground">name not recorded</span>}</span>
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">{r.cancelled_by}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Rows the system wrote itself — a service line removed on the order
            page, a treatment trimmed inside the appointment modal, or a legacy
            row with no reason at all. They are NOT cancellations and never
            touch the total; they are listed so an owner auditing one guest can
            account for every cancelled booking on the profile. */}
        <Card className="rounded-lg overflow-hidden" data-testid="cancellations-excluded-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-baseline justify-between">
              <div>
                <h2 className="text-sm font-semibold">Not counted as cancellations</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Bookings the system closed on its own (line removed from an invoice, treatment trimmed while editing the visit, or a legacy row with no reason). Excluded from every total above.
                </p>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">excluded</div>
                <div className="text-lg font-semibold" data-testid="cancellations-excluded-count">{data?.excluded_count ?? 0}</div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider">Closed</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Client</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Service</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Reason</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data || (data.excluded || []).length === 0) ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                    {loading ? "Loading…" : "None — every cancelled booking in this range is a genuine cancellation."}
                  </TableCell></TableRow>
                ) : data.excluded.map((r) => (
                  <TableRow key={r.id} data-testid={`cancel-excluded-row-${r.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtTime(r.cancelled_at)}</TableCell>
                    <TableCell className="text-sm">
                      {r.customer_id ? (
                        <Link to={`/staff/brand/customers/${r.customer_id}`} className="text-primary hover:underline">{r.customer_name}</Link>
                      ) : r.customer_name}
                    </TableCell>
                    <TableCell className="text-sm max-w-xs truncate" title={r.service_name}>{r.service_name}</TableCell>
                    <TableCell className="text-sm max-w-xs truncate" title={r.reason}>{r.reason}</TableCell>
                    <TableCell className="text-xs" data-testid={`cancel-excluded-source-${r.id}`}>
                      <span className="inline-block rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-700" title={r.source_label}>
                        {r.source_label}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
